import { useEffect, useRef, useState } from 'react';

interface Props {
  topElement: JSX.Element;
  bottomElement: JSX.Element;
  initialTopPercentage: number;
  minPercentage?: number;
  disableResize?: boolean;
}

const ResizableSplitContainer = (props: Props) => {
  const { topElement, bottomElement, initialTopPercentage, minPercentage, disableResize } = props;
  const [topHeight, setTopHeight] = useState(initialTopPercentage);
  const [dragging, setDragging] = useState(false); // ✅ NEW
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setTopHeight(initialTopPercentage);
  }, [initialTopPercentage]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (disableResize) return;

    setDragging(true);

    const startY = e.clientY;
    const startHeight = topHeight;

    const onMouseMove = (e: MouseEvent) => {
      if (containerRef.current) {
        const containerHeight = containerRef.current.offsetHeight;
        const newHeight = startHeight + ((e.clientY - startY) / containerHeight) * 100;

        setTopHeight(Math.max(minPercentage ?? 10, Math.min(100 - (minPercentage ?? 10), newHeight)));
      }
    };

    const onMouseUp = () => {
      setDragging(false);

      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const dividerColor = 'bg-primary';

  return (
    <div
      ref={containerRef}
      className={`flex flex-col h-full min-h-0 overflow-hidden space-y-3${dragging ? ' select-none' : ''}`}>
      <div
        data-testid="top-section"
        style={{ height: `${topHeight}%` }}
        className="min-h-0 overflow-y-auto overflow-x-hidden">
        {topElement}
      </div>
      <div className="cursor-row-resize space-y-[1px]" style={{ height: '6px' }} onMouseDown={handleMouseDown}>
        <div className={dividerColor} style={{ height: '1px' }}></div>
        <div className={dividerColor} style={{ height: '2px' }}></div>
        <div className={dividerColor} style={{ height: '1px' }}></div>
      </div>
      <div style={{ height: `${100 - topHeight}%` }} className="min-h-0 overflow-y-auto overflow-x-hidden">
        {bottomElement}
      </div>
    </div>
  );
};

export default ResizableSplitContainer;
